import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, ChevronDown, Wrench, Factory, Zap, KeyRound, Hammer, Croissant, Car, Smartphone, Trees, BadgeCheck, Star, Database, Camera } from "lucide-react";
import { SearchBar } from "@/components/SearchBar";
import { BusinessCard } from "@/components/BusinessCard";
import { api } from "@/lib/api";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

const HERO = "https://images.unsplash.com/photo-1676210133055-eab6ef033ce3?crop=entropy&cs=srgb&fm=jpg&q=80&w=1400";
const DEPANNAGE = [
  ["Électroménager", "Dépannage électroménager"],
  ["Chauffage", "Dépannage chauffage"],
  ["Climatisation", "Dépannage climatisation"],
  ["Voiture", "Dépannage voiture"],
];
const FABRICANTS = [
  ["Maisons", "Constructeur de maisons"],
  ["Bateaux", "Constructeur de bateaux"],
  ["Voitures", "Constructeur automobile"],
  ["Avions", "Constructeur avion aéronautique"],
  ["Camping-cars & remorques", "Fabricant camping-car remorque"],
  ["Motos", "Fabricant moto"],
  ["Meubles", "Fabricant de meubles"],
  ["Cuisines", "Fabricant de cuisines"],
  ["Fenêtres & portes", "Fabricant fenêtre porte"],
  ["Machines agricoles", "Fabricant machine agricole"],
  ["Vêtements", "Fabricant de vêtements"],
  ["Cosmétiques", "Fabricant cosmétique"],
];
const TRADES = [
  { label: "Électricien", q: "Électricien", icon: Zap },
  { label: "Serrurier", q: "Serrurier", icon: KeyRound },
  { label: "Menuisier", q: "Menuisier", icon: Hammer },
  { label: "Boulangerie", q: "Boulangerie", icon: Croissant },
  { label: "Garage auto", q: "Garage automobile", icon: Car },
  { label: "Réparation téléphone", q: "Réparation téléphone", icon: Smartphone },
  { label: "Paysagiste", q: "Paysagiste", icon: Trees },
];

function Hero({ stats }) {
  return (
    <section className="relative overflow-hidden bg-slate-950 text-white">
      <div className="grid-bg absolute inset-0" />
      <img src={HERO} alt="" className="absolute right-0 top-0 hidden h-full w-[42%] object-cover opacity-50 lg:block" />
      <div className="absolute inset-y-0 right-0 hidden w-[42%] bg-gradient-to-r from-slate-950 to-transparent lg:block" />
      <div className="relative mx-auto max-w-7xl px-4 pb-20 pt-16 sm:px-6 sm:pt-24 lg:px-8">
        <p className="fade-up inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-slate-300">
          <Database className="h-3.5 w-3.5 text-[#D97706]" />Répertoire officiel SIRENE + avis de la communauté
        </p>
        <h1 className="fade-up mt-6 max-w-3xl font-display text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl" style={{ animationDelay: "80ms" }}>
          Qui fait quoi,<br /><span className="text-[#60A5FA]">près de chez vous.</span>
        </h1>
        <p className="fade-up mt-5 max-w-xl text-base text-slate-300 sm:text-lg" style={{ animationDelay: "160ms" }}>
          Artisans, commerçants, fabricants et fournisseurs de toute la France. Trouvez le mieux noté, le plus proche, celui qui a la pièce en stock.
        </p>
        <div className="fade-up mt-10 max-w-4xl" style={{ animationDelay: "240ms" }}><SearchBar dark /></div>
        <div className="mt-8 flex flex-wrap gap-8 text-sm text-slate-400" data-testid="home-stats">
          <span><b className="font-display text-2xl text-white">100 %</b> des entreprises du répertoire SIRENE</span>
          <span><b className="font-display text-2xl text-white">{stats?.reviews ?? 0}</b> avis déposés</span>
          <span><b className="font-display text-2xl text-white">{stats?.pros ?? 0}</b> pros vérifiés</span>
        </div>
      </div>
    </section>
  );
}

function MenuTile({ id, label, icon: Icon, items }) {
  const navigate = useNavigate();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="group flex items-center justify-between rounded-xl border border-slate-200 bg-white p-4 text-left outline-none transition-[border-color,transform] hover:-translate-y-0.5 hover:border-[#1E40AF] data-[state=open]:border-[#1E40AF]" data-testid={`trade-${id}-menu`}>
        <span className="flex items-center gap-3 text-sm font-semibold text-slate-800"><Icon className="h-5 w-5 text-[#1E40AF]" />{label}</span>
        <ChevronDown className="h-4 w-4 text-slate-400 transition-transform group-data-[state=open]:rotate-180" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="max-h-80 w-60 overflow-auto">
        {items.map(([itemLabel, q]) => (
          <DropdownMenuItem key={itemLabel} onSelect={() => navigate(`/recherche?q=${encodeURIComponent(q)}`)} className="cursor-pointer py-2.5" data-testid={`${id}-option-${itemLabel.toLowerCase().replace(/[^a-zà-ÿ]+/g, "-")}`}>
            {itemLabel}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function Trades() {
  return (
    <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
      <h2 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">Recherches populaires</h2>
      <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <MenuTile id="depannage" label="Dépannage" icon={Wrench} items={DEPANNAGE} />
        <MenuTile id="fabricants" label="Fabricants" icon={Factory} items={FABRICANTS} />
        {TRADES.map(({ label, q, icon: Icon }) => (
          <Link key={label} to={`/recherche?q=${encodeURIComponent(q)}`} className="group flex items-center justify-between rounded-xl border border-slate-200 bg-white p-4 transition-[border-color,transform] hover:-translate-y-0.5 hover:border-[#1E40AF]" data-testid={`trade-link-${q.toLowerCase().replace(/\s+/g, "-")}`}>
            <span className="flex items-center gap-3 text-sm font-semibold text-slate-800"><Icon className="h-5 w-5 text-[#1E40AF]" />{label}</span>
            <ArrowRight className="h-4 w-4 text-slate-300 transition-transform group-hover:translate-x-1 group-hover:text-[#1E40AF]" />
          </Link>
        ))}
      </div>
    </section>
  );
}

function HowItWorks() {
  const steps = [
    { icon: Star, t: "Particuliers", d: "Recherchez un métier, un produit ou une marque. Comparez les avis de la communauté, puis notez le pro après son intervention." },
    { icon: BadgeCheck, t: "Professionnels", d: "Votre entreprise est déjà référencée grâce au SIRENE. Revendiquez-la avec votre SIRET, gratuitement, et répondez aux avis." },
    { icon: Camera, t: "Abonnement dès 4,90 €", d: "Ajoutez vos marques, compétences, produits en stock et photos. Soyez trouvé sur ce que vous faites vraiment." },
  ];
  return (
    <section className="border-y border-slate-200 bg-white">
      <div className="mx-auto grid max-w-7xl gap-10 px-4 py-16 sm:px-6 md:grid-cols-3 lg:px-8">
        {steps.map(({ icon: Icon, t, d }, i) => (
          <div key={t}>
            <span className="font-mono-id text-xs text-slate-400">0{i + 1}</span>
            <Icon className="mt-3 h-7 w-7 text-[#1E40AF]" />
            <h3 className="mt-4 font-display text-xl font-bold">{t}</h3>
            <p className="mt-2 text-sm leading-relaxed text-slate-600">{d}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

export default function Home() {
  const [stats, setStats] = useState(null);
  const [featured, setFeatured] = useState([]);
  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/businesses/featured").then((r) => setFeatured(r.data)).catch(() => {});
  }, []);
  return (
    <div data-testid="home-page">
      <Hero stats={stats} />
      <Trades />
      {featured.length > 0 && (
        <section className="mx-auto max-w-7xl px-4 pb-16 sm:px-6 lg:px-8" data-testid="featured-section">
          <h2 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">Les mieux notés</h2>
          <div className="mt-8 grid gap-4 lg:grid-cols-2">{featured.map((b) => <BusinessCard key={b.id} b={b} />)}</div>
        </section>
      )}
      <HowItWorks />
      <section className="mx-auto max-w-7xl px-4 pt-16 sm:px-6 lg:px-8">
        <div className="flex flex-col items-start justify-between gap-6 rounded-2xl bg-[#1E40AF] p-8 text-white sm:p-12 md:flex-row md:items-center">
          <div>
            <h2 className="font-display text-2xl font-bold sm:text-3xl">Vous êtes un professionnel ?</h2>
            <p className="mt-2 text-blue-100">Revendiquez votre fiche en 1 minute avec votre numéro SIRET.</p>
          </div>
          <Link to="/mon-espace" className="rounded-full bg-white px-6 py-3 font-semibold text-[#1E40AF] transition-colors hover:bg-slate-900 hover:text-white" data-testid="home-pro-cta">
            Revendiquer ma fiche
          </Link>
        </div>
      </section>
    </div>
  );
}
